import * as React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  UserRoundPlus,
  UsersRound,
  X,
} from 'lucide-react';
import { cn, formatCompactNumber } from '@/lib/utils';
import { formatGrowthDate, type GrowthPoint } from './growth-model';
import type { ChartMetric } from './line-chart';
import {
  growthDetailRows,
  growthTooltipPosition,
  type GrowthDetailRow,
} from './growth-hover';
import {
  growthLabelTransform,
  growthBarBounds,
  growthIndexAtX,
  growthPointX,
} from './growth-geometry';
import { formatGrowthTick, growthAxisTicks } from './growth-axis';
import { growthBars, growthBarHeight, growthUserMarker } from './growth-bars';
import {
  growthLabelPlotWidth,
  growthPaidLabel,
  growthUserCounts,
  growthUserLabelLayout,
  growthUserLabelWidth,
} from './growth-labels';
import { growthBucketLabel, type GrowthAnomaly } from './growth-anomalies';

const HEIGHT = 340;
const TOP = 54;
const BOTTOM = 316;
const number = (value: number | null) =>
  value === null ? 'Unavailable' : value.toLocaleString();
const usd = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(value);
const detailValue = (row: GrowthDetailRow) =>
  row.value === null ? '—' : row.currency ? usd(row.value) : number(row.value);

export const GrowthChart = ({
  points,
  metric,
  metricLabel,
  bucketMinutes,
  showSignups,
  showPaid,
  showRevenue,
  showAppUsers = false,
  goalLabel = 'Signups',
  anomalies = [],
  onExplainAnomaly,
  isExplainingAnomaly = false,
}: {
  points: GrowthPoint[];
  metric: ChartMetric;
  metricLabel: string;
  bucketMinutes: number;
  showSignups: boolean;
  showPaid: boolean;
  showRevenue: boolean;
  showAppUsers?: boolean;
  goalLabel?: string;
  anomalies?: GrowthAnomaly[];
  onExplainAnomaly?: (anomaly: GrowthAnomaly) => void | Promise<void>;
  isExplainingAnomaly?: boolean;
}) => {
  const [active, setActive] = React.useState<number | null>(null);
  const [pinned, setPinned] = React.useState(false);
  const [hoverY, setHoverY] = React.useState<number | null>(null);
  const plotRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = React.useState(1000);
  const [visiblePlot, setVisiblePlot] = React.useState({
    left: 0,
    width: 1000,
  });
  const updateVisiblePlot = React.useCallback(() => {
    const plot = plotRef.current?.getBoundingClientRect();
    const scroll = scrollRef.current?.getBoundingClientRect();
    if (!plot || !scroll) {
      return;
    }
    const left = Math.max(0, scroll.left - plot.left);
    setVisiblePlot({
      left,
      width: Math.max(0, Math.min(plot.width, scroll.right - plot.left) - left),
    });
  }, []);
  const hoverRef = React.useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = React.useState(180);
  const axisRef = React.useRef<HTMLDivElement>(null);
  const axisLabels = React.useMemo(
    () =>
      points.map((point) => formatGrowthTick(point.timestamp, bucketMinutes)),
    [points, bucketMinutes],
  );
  const [labelWidths, setLabelWidths] = React.useState<number[]>([]);
  React.useLayoutEffect(() => {
    const axis = axisRef.current;
    const context = document.createElement('canvas').getContext('2d');
    if (!axis || !context) {
      return;
    }
    const measure = () => {
      const font = getComputedStyle(axis);
      context.font =
        font.fontWeight + ' ' + font.fontSize + ' ' + font.fontFamily;
      setLabelWidths(
        axisLabels.map((label) => Math.ceil(context.measureText(label).width)),
      );
    };
    measure();
    document.fonts.addEventListener('loadingdone', measure);
    return () => document.fonts.removeEventListener('loadingdone', measure);
  }, [axisLabels]);
  const axisTicks = growthAxisTicks(
    axisLabels.map((label, i) => labelWidths[i] ?? label.length * 8),
    bucketMinutes,
    plotWidth,
  );
  React.useEffect(() => {
    if (!plotRef.current) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (plotRef.current) {
        setPlotWidth(plotRef.current.getBoundingClientRect().width);
      }
      updateVisiblePlot();
    });
    observer.observe(plotRef.current);
    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }
    return () => observer.disconnect();
  }, [points.length, updateVisiblePlot]);

  const tooltipId = React.useId();
  const barSeries = growthBars(points, {
    signups: showSignups,
    newAppUsers: showAppUsers,
    newPaid: showPaid,
    recordedRevenueUsd: showRevenue,
  });
  const max =
    Math.ceil(Math.max(4, ...points.map((point) => point[metric])) / 4) * 4;
  const barMax =
    Math.ceil(Math.max(4, ...barSeries.map((series) => series.maximum)) / 4) *
    4;
  const hasBars = barSeries.length > 0;
  const hasPayments = barSeries.some((series) => series.currency);
  const visibleAnomalies = React.useMemo(
    () =>
      anomalies.filter(
        (anomaly) =>
          (anomaly.metric === 'signups' && showSignups) ||
          (anomaly.metric === 'visitors' && metric === 'visitors'),
      ),
    [anomalies, metric, showSignups],
  );
  const anomalyByTimestamp = React.useMemo(() => {
    const byTimestamp = new Map<string, GrowthAnomaly>();
    for (const anomaly of visibleAnomalies) {
      if (!byTimestamp.has(anomaly.timestamp)) {
        byTimestamp.set(anomaly.timestamp, anomaly);
      }
    }
    return byTimestamp;
  }, [visibleAnomalies]);
  const minimumPlotWidth = growthLabelPlotWidth(points, {
    signups: showSignups,
    newAppUsers: showAppUsers,
    paid: showPaid && !showRevenue,
    revenue: showRevenue,
  });
  const x = (index: number) => growthPointX(index, points.length, plotWidth);
  const y = (value: number) => TOP + (1 - value / max) * (BOTTOM - TOP);
  const dailyCounts = points.map((point) =>
    growthUserCounts(
      point,
      {
        signups: showSignups,
        newAppUsers: showAppUsers,
      },
      goalLabel,
    ),
  );
  const userLabels = growthUserLabelLayout(
    points.map((point, i) =>
      dailyCounts[i].length
        ? {
            x: x(i),
            y: y(point[metric]),
            width: growthUserLabelWidth(dailyCounts[i]),
            height: dailyCounts[i].length * 18,
          }
        : null,
    ),
    { width: plotWidth, height: BOTTOM },
  );
  const line = points
    .map(
      (point, index) =>
        (index === 0 ? 'M' : 'L') + x(index) + ',' + y(point[metric]),
    )
    .join(' ');
  const area = points.length
    ? line +
      ' L' +
      x(points.length - 1) +
      ',' +
      BOTTOM +
      ' L' +
      x(0) +
      ',' +
      BOTTOM +
      ' Z'
    : '';
  const index =
    active === null || !points.length
      ? null
      : Math.min(active, points.length - 1);
  const selected = index === null ? undefined : points[index];
  const selectedAnomaly = selected
    ? anomalyByTimestamp.get(selected.timestamp)
    : undefined;
  const hourly = bucketMinutes < 1440;
  const select = (next: number, pointerY: number | null = null) => {
    setActive(Math.max(0, Math.min(points.length - 1, next)));
    setHoverY(pointerY);
    setPinned(true);
  };
  const dismiss = () => {
    setPinned(false);
    setActive(null);
    setHoverY(null);
  };
  const rows = selected
    ? growthDetailRows(
        selected,
        metric,
        metricLabel,
        {
          signups: showSignups,
          paid: showPaid && !showRevenue,
          appUsers: showAppUsers,
          revenue: showRevenue,
        },
        goalLabel,
      )
    : [];
  const hasSelection = Boolean(selected);
  React.useEffect(() => {
    if (!pinned || index === null || !plotRef.current || !scrollRef.current) {
      return;
    }
    const plot = plotRef.current.getBoundingClientRect();
    const scroll = scrollRef.current.getBoundingClientRect();
    const anchor = plot.left + growthPointX(index, points.length, plot.width);
    if (anchor < scroll.left + 24) {
      scrollRef.current.scrollLeft += anchor - scroll.left - 24;
    } else if (anchor > scroll.right - 24) {
      scrollRef.current.scrollLeft += anchor - scroll.right + 24;
    }
    updateVisiblePlot();
  }, [pinned, index, points.length, updateVisiblePlot]);
  React.useLayoutEffect(() => {
    const element = hoverRef.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setTooltipHeight(element.offsetHeight),
    );
    setTooltipHeight(element.offsetHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasSelection, pinned]);
  React.useEffect(() => {
    if (!hasSelection) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPinned(false);
        setActive(null);
        if (hoverRef.current?.contains(document.activeElement)) {
          plotRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasSelection]);

  if (!points.length) {
    return (
      <p className="px-6 py-20 text-center text-sm text-ink-muted">
        No traffic in this period. Your first visit will start the timeline.
      </p>
    );
  }
  const dateLabel = selected
    ? (bucketMinutes > 10080
        ? '4 weeks from '
        : bucketMinutes > 1440
          ? 'Week of '
          : '') + formatGrowthDate(selected.timestamp, hourly, true)
    : '';
  const tooltipPosition = growthTooltipPosition(
    {
      x: x(index ?? 0) - visiblePlot.left,
      y: hoverY ?? y(selected?.[metric] ?? 0),
    },
    { width: visiblePlot.width, height: HEIGHT },
    tooltipHeight,
  );

  return (
    <div className="px-4 pb-4 sm:px-6">
      <div className="mb-2 flex justify-between gap-2 text-xs text-ink-muted">
        <span>{metricLabel}</span>
        {hasBars && <span>{hasPayments ? 'Payments · USD' : 'New paid'}</span>}
      </div>
      <div
        ref={scrollRef}
        onScroll={updateVisiblePlot}
        className="overflow-x-auto overscroll-x-contain pb-2"
        tabIndex={0}
        role="region"
        aria-label="Daily counts timeline. Scroll horizontally for more dates."
      >
        <div
          className={cn('relative pl-9', hasBars && 'pr-9')}
          style={{ minWidth: minimumPlotWidth + (hasBars ? 72 : 36) }}
        >
          <div
            className="pointer-events-none absolute left-0 top-0 w-8 text-right text-xs text-ink-muted"
            aria-hidden="true"
          >
            {[0, 1, 2, 3, 4].map((tick) => (
              <span
                key={tick}
                className="absolute right-0 -translate-y-1/2"
                style={{ top: TOP + (tick * (BOTTOM - TOP)) / 4 }}
              >
                {formatCompactNumber(max * (1 - tick / 4))}
              </span>
            ))}
          </div>
          {hasBars && (
            <div
              className="pointer-events-none absolute right-0 top-0 w-7 text-xs text-ink-muted"
              aria-label={
                hasPayments
                  ? 'Payments in USD, right axis'
                  : 'New paid count, right axis'
              }
            >
              {[0, 1, 2, 3, 4].map((tick) => (
                <span
                  key={tick}
                  className="absolute left-0 -translate-y-1/2"
                  style={{ top: TOP + (tick * (BOTTOM - TOP)) / 4 }}
                >
                  {hasPayments ? '$' : ''}
                  {formatCompactNumber(barMax * (1 - tick / 4))}
                </span>
              ))}
            </div>
          )}
          <div
            ref={plotRef}
            className="relative touch-pan-y rounded focus-visible:outline-accent"
            tabIndex={0}
            role="group"
            aria-label={
              metricLabel +
              ' and growth timeline. Use left and right arrow keys to inspect dates. Escape closes details.'
            }
            aria-describedby={selected ? tooltipId : undefined}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) {
                return;
              }
              if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                event.preventDefault();
                select(
                  (index ?? (event.key === 'ArrowRight' ? -1 : points.length)) +
                    (event.key === 'ArrowRight' ? 1 : -1),
                );
              }
              if (event.key === 'Home') {
                event.preventDefault();
                select(0);
              }
              if (event.key === 'End') {
                event.preventDefault();
                select(points.length - 1);
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                select(index ?? 0);
              }
            }}
            onPointerMove={(event) => {
              if (pinned || event.pointerType === 'touch') {
                return;
              }
              const bounds = event.currentTarget.getBoundingClientRect();
              setActive(
                growthIndexAtX(
                  event.clientX - bounds.left,
                  points.length,
                  bounds.width,
                ),
              );
              setHoverY(event.clientY - bounds.top);
            }}
            onPointerLeave={() => {
              if (!pinned) {
                setActive(null);
              }
            }}
            onClick={(event) => {
              if ((event.target as Element).closest('[data-chart-details]')) {
                return;
              }
              const bounds = event.currentTarget.getBoundingClientRect();
              select(
                growthIndexAtX(
                  event.clientX - bounds.left,
                  points.length,
                  bounds.width,
                ),
                event.clientY - bounds.top,
              );
            }}
          >
            <svg
              data-growth-plot
              viewBox={'0 0 ' + plotWidth + ' ' + HEIGHT}
              style={{ height: HEIGHT }}
              className="w-full overflow-visible"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {[0, 1, 2, 3, 4].map((tick) => (
                <line
                  key={tick}
                  x1="0"
                  x2={plotWidth}
                  y1={TOP + (tick * (BOTTOM - TOP)) / 4}
                  y2={TOP + (tick * (BOTTOM - TOP)) / 4}
                  className="stroke-border"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              <path d={area} className="fill-accent/8" />
              {barSeries.map((series) => (
                <g key={series.key} data-growth-series={series.key}>
                  {points.map((point, i) => {
                    const height = growthBarHeight(
                      point[series.key],
                      barMax,
                      BOTTOM - TOP,
                    );
                    if (height === null || height === 0) {
                      return null;
                    }
                    const bounds = growthBarBounds(i, points.length, plotWidth);
                    return (
                      <rect
                        key={point.timestamp}
                        data-growth-bar={point.timestamp}
                        data-value={point[series.key]}
                        {...bounds}
                        y={BOTTOM - height}
                        height={height}
                        rx={Math.min(3, bounds.width / 3)}
                        className={series.fill}
                        opacity={index === null || index === i ? 1 : 0.4}
                      />
                    );
                  })}
                </g>
              ))}
              <path
                d={line}
                fill="none"
                className="stroke-accent"
                strokeWidth="2.5"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {points.map((point, i) => {
                const marker = growthUserMarker(point, {
                  signups: showSignups,
                  newAppUsers: showAppUsers,
                });
                if (!marker) {
                  return null;
                }
                return (
                  <circle
                    key={point.timestamp}
                    data-growth-users={point.timestamp}
                    cx={x(i)}
                    cy={y(point[metric])}
                    r={Math.min(
                      3.5,
                      plotWidth / Math.max(1, points.length - 1) / 3,
                    )}
                    className={cn(
                      'stroke-surface',
                      marker === 'signup' ? 'fill-accent' : 'fill-ink-muted',
                    )}
                    strokeWidth="1.5"
                    opacity={index === null || index === i ? 1 : 0.5}
                  />
                );
              })}
              {points.map((point, i) => {
                const anomaly = anomalyByTimestamp.get(point.timestamp);
                if (!anomaly) {
                  return null;
                }
                return (
                  <g
                    key={`anomaly-${point.timestamp}`}
                    data-growth-anomaly={point.timestamp}
                    data-anomaly-metric={anomaly.metric}
                  >
                    <circle
                      cx={x(i)}
                      cy={y(point[metric])}
                      r="8"
                      className="fill-surface stroke-accent"
                      strokeWidth="1.5"
                      opacity={index === null || index === i ? 1 : 0.55}
                    />
                    <circle
                      cx={x(i)}
                      cy={y(point[metric])}
                      r="3"
                      className="fill-accent"
                      opacity={index === null || index === i ? 1 : 0.55}
                    />
                  </g>
                );
              })}
            </svg>
            {points.map((point, i) => {
              const counts = dailyCounts[i];
              const userLabel = userLabels[i];
              const usersBounds = userLabel
                ? {
                    top: userLabel.top,
                    bottom: userLabel.top + userLabel.height,
                  }
                : null;
              const bar = barSeries[0];
              const barValue = bar ? point[bar.key] : null;
              const barHeight =
                barValue !== null
                  ? growthBarHeight(barValue, barMax, BOTTOM - TOP)
                  : null;
              const barBounds = growthBarBounds(i, points.length, plotWidth);
              const paidLabel = barHeight
                ? growthPaidLabel(BOTTOM - barHeight, BOTTOM, usersBounds)
                : null;
              const day = formatGrowthDate(point.timestamp, hourly, true);
              return (
                <React.Fragment key={point.timestamp}>
                  {userLabel && (
                    <div
                      data-growth-daily-users={point.timestamp}
                      className="pointer-events-none absolute text-xs font-medium tabular-nums"
                      style={{
                        left: userLabel.left,
                        top: userLabel.top,
                      }}
                    >
                      {counts.map((count) => (
                        <span
                          key={count.key}
                          role="img"
                          aria-label={`${day}: ${count.value} ${count.label.toLowerCase()}`}
                          className={cn(
                            'flex h-[18px] w-fit items-center gap-0.5 bg-surface/90',
                            count.key === 'signups'
                              ? 'text-accent'
                              : 'text-ink',
                          )}
                        >
                          {count.key === 'signups' ? (
                            <UserRoundPlus
                              aria-hidden="true"
                              className="size-3"
                            />
                          ) : (
                            <UsersRound aria-hidden="true" className="size-3" />
                          )}
                          {formatCompactNumber(count.value)}
                        </span>
                      ))}
                    </div>
                  )}
                  {paidLabel && barValue !== null && (
                    <span
                      data-growth-daily-paid={point.timestamp}
                      role="img"
                      aria-label={`${day}: ${bar?.currency ? usd(barValue) + ' payments' : barValue + ' new paid'}`}
                      className={cn(
                        'pointer-events-none absolute whitespace-nowrap text-xs font-semibold tabular-nums',
                        paidLabel.inside &&
                          !bar?.currency &&
                          formatCompactNumber(barValue).length * 8 <=
                            barBounds.width
                          ? 'text-positive-ink'
                          : 'bg-surface/95 px-0.5 text-ink',
                      )}
                      style={{
                        left: barBounds.x + barBounds.width / 2,
                        top: paidLabel.top,
                        transform: growthLabelTransform(
                          barBounds.x + barBounds.width / 2,
                          plotWidth,
                        ),
                      }}
                    >
                      {bar?.currency
                        ? usd(barValue)
                        : formatCompactNumber(barValue)}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
            {((selected && index !== null) || points.length === 1) && (
              <span
                className="pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-accent"
                style={{
                  left: growthPointX(index ?? 0, points.length, 100) + '%',
                  top: y(points[index ?? 0][metric]),
                }}
              />
            )}
            {selected && index !== null && (
              <>
                <div
                  className="pointer-events-none absolute bottom-0 border-l border-dashed border-ink-muted/50"
                  style={{
                    left: growthPointX(index, points.length, 100) + '%',
                    top: TOP,
                  }}
                  aria-hidden="true"
                />
                <div
                  ref={hoverRef}
                  id={tooltipId}
                  data-chart-details
                  role={pinned ? 'region' : 'tooltip'}
                  aria-label={
                    pinned ? 'Selected date details' : 'Date activity'
                  }
                  aria-live={pinned ? 'polite' : undefined}
                  className={cn(
                    'absolute z-20 rounded-lg border border-border-strong bg-surface p-3 text-xs',
                    !pinned && 'pointer-events-none',
                  )}
                  style={{
                    ...tooltipPosition,
                    left: visiblePlot.left + tooltipPosition.left,
                  }}
                >
                  {pinned && (
                    <div className="mb-2 flex items-center justify-between border-b border-border pb-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          aria-label="Previous date"
                          disabled={index === 0}
                          onClick={() => select(index - 1, hoverY)}
                          className="inline-flex size-11 items-center justify-center rounded text-ink-muted hover:bg-surface-muted disabled:opacity-30 sm:size-8"
                        >
                          <ChevronLeft className="size-4" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next date"
                          disabled={index === points.length - 1}
                          onClick={() => select(index + 1, hoverY)}
                          className="inline-flex size-11 items-center justify-center rounded text-ink-muted hover:bg-surface-muted disabled:opacity-30 sm:size-8"
                        >
                          <ChevronRight className="size-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        aria-label="Close date details"
                        onClick={() => {
                          dismiss();
                          plotRef.current?.focus();
                        }}
                        className="inline-flex size-11 items-center justify-center rounded text-ink-muted hover:bg-surface-muted sm:size-8"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  )}
                  <p className="mb-2 font-semibold text-ink">
                    {dateLabel} · UTC
                  </p>
                  {rows.map((row) => (
                    <p
                      key={row.label}
                      className="flex justify-between gap-3 py-0.5 text-ink-muted"
                    >
                      <span>{row.label}</span>
                      <strong className="font-medium tabular-nums text-ink">
                        {detailValue(row)}
                      </strong>
                    </p>
                  ))}
                  {showAppUsers && selected.newAppUsersIncomplete && (
                    <p className="mt-2 text-ink-muted">
                      New app users still updating.
                    </p>
                  )}
                  {selectedAnomaly && (
                    <div className="mt-2 border-t border-border pt-2">
                      <div className="flex items-start gap-2">
                        <Sparkles
                          aria-hidden="true"
                          className="mt-0.5 size-3.5 shrink-0 text-accent"
                        />
                        <div>
                          <p className="font-medium text-ink">
                            Unusual {selectedAnomaly.metricLabel.toLowerCase()}
                          </p>
                          <p className="mt-0.5 text-ink-muted">
                            {formatCompactNumber(selectedAnomaly.value)} vs{' '}
                            {formatCompactNumber(selectedAnomaly.previousValue)}{' '}
                            previous {growthBucketLabel(bucketMinutes)}
                          </p>
                        </div>
                      </div>
                      {pinned && onExplainAnomaly ? (
                        <button
                          type="button"
                          disabled={isExplainingAnomaly}
                          onClick={(event) => {
                            event.stopPropagation();
                            void onExplainAnomaly(selectedAnomaly);
                          }}
                          className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md text-xs font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 sm:min-h-8"
                        >
                          <Sparkles aria-hidden="true" className="size-3.5" />
                          {isExplainingAnomaly
                            ? 'Investigating…'
                            : 'Explain this change'}
                        </button>
                      ) : onExplainAnomaly ? (
                        <p className="mt-2 text-[11px] text-ink-subtle">
                          Click the point to explain
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div
            ref={axisRef}
            className="relative mt-2 h-4 text-xs text-ink-muted"
            aria-hidden="true"
          >
            {axisTicks.map((i) => (
              <span
                key={i}
                className="absolute truncate whitespace-nowrap"
                style={{
                  left: growthPointX(i, points.length, 100) + '%',
                  transform: growthLabelTransform(
                    growthPointX(i, points.length, plotWidth),
                    plotWidth,
                  ),
                  maxWidth: plotWidth,
                }}
              >
                {axisLabels[i]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
